import type { ClientMessage, ServerMessage, SnapshotFrame, VoiceData } from "@obean/protocol";
import {
  CloseCode,
  PING,
  PROTOCOL_VERSION,
  ROOM_PROTOCOL,
  TOKEN_PREFIX,
  closeAction,
  decodeServerFrame,
  encodeMessage,
  parseServerMessage,
} from "@obean/protocol";
import { signal } from "@preact/signals";
import { ApiError, ON_HOLD, api } from "./api.js";
import { departureNote } from "./departures.js";
import { CHECK_EVERY_MS, LivenessCheck } from "./liveness.js";

export type RoomState = Extract<ServerMessage, { type: "room.state" }>;
export type MatchLoad = Extract<ServerMessage, { type: "match.load" }>;
export type MatchEnd = Extract<ServerMessage, { type: "match.end" }>;
export type ChatMsg = Extract<ServerMessage, { type: "chat.msg" }>;
export type ServerError = Extract<ServerMessage, { type: "error" }>;
export type RoomMoved = Extract<ServerMessage, { type: "room.moved" }>;

/** Chat messages kept on screen (nothing is kept anywhere else: the room relays and forgets). */
const CHAT_KEPT = 100;

export type ConnectionStatus =
  | { kind: "connecting" }
  | { kind: "connected" }
  /** Lost the room (P2-RT-06): trying again, since `since`, while the room holds the seat. */
  | { kind: "reconnecting"; since: number }
  /**
   * `code` is the socket's close code, or 0 when the api refused (`reason` is its error code, and
   * `details` the rest of its reply, e.g. the free hosting for `hosting_limit`).
   */
  | { kind: "closed"; code: number; reason: string; details?: Record<string, unknown> };

/** How long after a drop the client keeps trying: as long as the room holds the seat. */
export const RECONNECT_WINDOW_MS = 30_000;
/** No frame for this long during a match: the connection has stalled, and inputs are dropped. */
export const STALE_INPUT_MS = 1_000;
/** Waits before each try (ADR 0004), then every second (capped at 1 s: founder, 2026-10-04). */
const BACKOFF_MS = [0, 250, 500, 1000];

/**
 * One room socket (protocol v1): a room token from the api, the socket through the same-origin
 * router with the token offered as a subprotocol, hello, then the room as it changes. A token that
 * expired on the way (4003) gets one fresh try. During a match it also carries the match messages
 * and the binary snapshot frames (P2-RT-05).
 *
 * Once in the room, a dropped connection is tried again with backoff for as long as the room holds
 * the seat, asking for it back (`hello.resume`); coming back to the tab or the network tries at once
 * (P2-RT-06). A connection that dies without closing is noticed too: the room is pinged when quiet,
 * and asked straight away when the page or the network comes back (P2-WEB-06, liveness.ts).
 */
export class RoomConnection {
  readonly status = signal<ConnectionStatus>({ kind: "connecting" });
  readonly room = signal<RoomState | null>(null);
  /** This client's seat, once welcomed. */
  readonly seat = signal<string | null>(null);
  /** The match this client was last told to load (`match.load`). */
  readonly match = signal<MatchLoad | null>(null);
  /** How the last match ended (`match.end`), until the next one loads. */
  readonly ended = signal<MatchEnd | null>(null);
  /** Chat since this page joined, oldest first (P2-RT-09). */
  readonly chat = signal<readonly ChatMsg[]>([]);
  /** Someone left for a new room and asked everyone along (#616), until followed or put away. */
  readonly moved = signal<RoomMoved | null>(null);
  /** Who has just gone from the room, and who hosts now (#616, `departureNote`), until put away. */
  readonly departed = signal<string | null>(null);
  /** Seats this page removed: they're not announced as having left. */
  readonly #removed = new Set<string>();
  /** The room's latest refusal (`error`), e.g. a chat message over the rate limit. */
  readonly error = signal<ServerError | null>(null);
  /**
   * Seats muted on this page (P2-SAFE-01): their chat is hidden here. Local only, like the safety
   * doc says; blocking also mutes, and is the one that lasts.
   */
  readonly muted = signal<ReadonlySet<string>>(new Set());
  /** Gets voice signals from other seats (P2-VOICE-02; a listener, not a signal). */
  onVoiceSignal: ((from: string, data: VoiceData) => void) | null = null;
  /** Gets the snapshot frames, 20 a second during a match (a listener, not a signal). */
  onFrame: ((frame: SnapshotFrame) => void) | null = null;

  #socket = 0;
  /**
   * Which socket this is: each connect counts up. A match's inputs are numbered per socket (the room
   * starts a returning player's count afresh, P2-RT-06), so a driver only sends on the socket it
   * was made for; a late input from the old one would otherwise make the new 1, 2, 3 look stale.
   */
  get socket(): number {
    return this.#socket;
  }
  /** The latest frame of the current match, for a listener that starts late (after loading). */
  latestFrame: SnapshotFrame | null = null;
  #ws: WebSocket | null = null;
  /** When this socket last brought a frame: inputs into a stalled one are stale (below). */
  #frameAt: number | null = null;
  #retried = false;
  #done = false;
  /** The seat to ask back for after a drop (`welcome.seat`). */
  #resume: { seat: string; secret: string } | null = null;
  #lostAt: number | null = null;
  #attempt = 0;
  #retryTimer: ReturnType<typeof setTimeout> | undefined;
  #liveness: LivenessCheck | null = null;
  #watchdog: ReturnType<typeof setInterval> | undefined;
  #watching = false;
  /** Rejoined by itself after the reconnect window (#644): once, until it's back in. */
  #autoRejoined = false;
  readonly #wake = () => {
    if (document.visibilityState === "hidden") return;
    const status = this.status.value.kind;
    if (canRejoin(this.status.value) && !this.#autoRejoined) {
      // Back after the room stopped holding the seat: straight back in, as a member (#644).
      this.#autoRejoined = true;
      this.rejoin();
    } else if (status === "reconnecting") {
      this.#attempt = 0; // back in front, or back online: try now
      this.#retry();
    } else if (status === "connected" && this.#liveness) {
      this.#liveness.wake(Date.now()); // the socket may have died meanwhile: ask
      this.#ping();
    }
  };

  constructor(
    readonly roomId: string,
    /** An invite link's secret, for someone who isn't a member yet. */
    readonly secret?: string,
  ) {}

  async open(): Promise<void> {
    this.#done = false;
    if (this.status.value.kind !== "reconnecting") this.status.value = { kind: "connecting" };
    let token: { token: string; path: string };
    try {
      token = await api.roomToken(this.roomId, this.secret);
    } catch (err) {
      if (this.#isDone()) return;
      // Offline, the api down, or over its rate limit for a moment (429): keep trying.
      const final = err instanceof ApiError && err.status < 500 && err.status !== 429;
      if (this.#lostAt !== null && !final) {
        this.#reconnect();
        return;
      }
      this.status.value = {
        kind: "closed",
        code: 0,
        reason: err instanceof ApiError ? err.code : "network",
        ...(err instanceof ApiError ? { details: err.body } : {}),
      };
      return;
    }
    if (this.#isDone()) return; // left while the token was on its way
    const scheme = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${scheme}://${location.host}${token.path}`, [
      ROOM_PROTOCOL,
      TOKEN_PREFIX + token.token,
    ]);
    this.#ws = ws;
    this.#frameAt = null; // a new socket: judged by its own frames
    this.#socket++;
    ws.binaryType = "arraybuffer"; // snapshot frames (browsers deliver Blobs otherwise)
    ws.addEventListener("open", () => {
      const resume = this.#resume ? { resume: this.#resume } : {};
      ws.send(encodeMessage({ type: "hello", v: PROTOCOL_VERSION, build: "web", ...resume }));
    });
    ws.addEventListener("message", (e) => {
      this.#receive(e.data);
    });
    ws.addEventListener("close", (e) => {
      this.#closed(ws, e.code, e.reason);
    });
  }

  /** Sends a message if the socket is open (a message sent while it's down is dropped). */
  send(msg: ClientMessage, now = Date.now()): void {
    if (this.#ws?.readyState !== WebSocket.OPEN) return;
    if (msg.type === "room.remove") this.#removed.add(msg.seat);
    // A match sends 20 frames a second. No frame for a while means the connection has stalled
    // (no signal, a sleeping network) even though the socket still says open: inputs sent now
    // would queue up and arrive all at once when it recovers, stale, and enough of them to trip
    // the room's flood limit (4008) and lose the seat (daily rehearsal, 2026-10-24). Drop them;
    // the game sends fresh ones once frames flow again.
    if (msg.type === "match.in" && this.#frameAt !== null && now - this.#frameAt > STALE_INPUT_MS)
      return;
    this.#ws.send(encodeMessage(msg));
  }

  setMuted(seat: string, muted: boolean): void {
    const next = new Set(this.muted.value);
    if (muted) next.add(seat);
    else next.delete(seat);
    this.muted.value = next;
  }

  /**
   * Leave the room for good (frees the seat), then close. `to`: a new room this player just made,
   * whose invite the others are offered (#616).
   */
  leave(to?: { room: string; secret: string }): void {
    if (this.#ws?.readyState === WebSocket.OPEN)
      this.#ws.send(encodeMessage({ type: "room.leave", ...(to ? { to } : {}) }));
    this.close();
  }

  /**
   * Back into the room after the connection ended (#644): a fresh seat as a member, like opening
   * the room's link again. A match this page was in has gone on without it (a bot plays the slot,
   * ADR 0004), so it's forgotten: the room shows the next one.
   */
  rejoin(): void {
    clearTimeout(this.#retryTimer);
    this.#ws = null;
    this.#resume = null;
    this.#lostAt = null;
    this.#attempt = 0;
    this.#retried = false;
    this.match.value = null;
    this.ended.value = null;
    this.latestFrame = null;
    this.status.value = { kind: "connecting" };
    this.#watch();
    void this.open();
  }

  /** Close this tab's connection (the seat stays reserved for a while). */
  close(): void {
    this.#done = true;
    clearTimeout(this.#retryTimer);
    this.#stopChecking();
    this.#unwatch();
    this.#ws?.close(1000);
    this.#ws = null;
  }

  /** A method, so the check after an await isn't narrowed away. */
  #isDone(): boolean {
    return this.#done;
  }

  #receive(data: unknown): void {
    this.#liveness?.heard(Date.now()); // anything from the room, a pong or a frame, is a sign of life
    if (data instanceof ArrayBuffer) {
      const frame = decodeServerFrame(data);
      if (frame.kind !== "snapshot") return; // a newer server's frame kind: skipped
      this.#frameAt = Date.now();
      this.latestFrame = frame.frame;
      this.onFrame?.(frame.frame);
      return;
    }
    const parsed = parseServerMessage(typeof data === "string" ? data : "");
    if (!parsed.ok) return;
    const msg = parsed.msg;
    if (msg.type === "welcome") {
      this.seat.value = msg.seat.id;
      this.#resume = { seat: msg.seat.id, secret: msg.seat.secret };
      this.#lostAt = null;
      this.#attempt = 0;
      this.#autoRejoined = false;
      this.#startChecking();
      this.#watch();
      this.status.value = { kind: "connected" };
    } else if (msg.type === "room.state") {
      const before = this.room.value;
      this.room.value = msg;
      const skip = new Set(this.#removed);
      if (this.moved.value) skip.add(this.moved.value.seat);
      const note = before ? departureNote(before, msg, this.seat.value, skip) : null;
      if (note) this.departed.value = note;
    } else if (msg.type === "room.moved") {
      this.moved.value = msg;
    } else if (msg.type === "match.load") {
      this.ended.value = null;
      this.latestFrame = null;
      this.match.value = msg;
    } else if (msg.type === "match.end") {
      this.ended.value = msg;
    } else if (msg.type === "voice.signal") {
      this.onVoiceSignal?.(msg.from, msg.data);
    } else if (msg.type === "chat.msg") {
      this.chat.value = [...this.chat.value, msg].slice(-CHAT_KEPT);
    } else if (msg.type === "error") {
      this.error.value = msg;
    }
  }

  #closed(ws: WebSocket, code: number, reason: string): void {
    if (ws !== this.#ws || this.#done) return;
    this.#stopChecking();
    if (code === CloseCode.unauthorized && !this.#retried) {
      this.#retried = true;
      void this.open();
      return;
    }
    // In the room before, and a drop the client may come back from: try again (P2-RT-06).
    if (this.#resume && closeAction(code) === "reconnect") {
      this.#reconnect();
      return;
    }
    this.status.value = { kind: "closed", code, reason };
  }

  /** The next try, after the backoff, unless the room has stopped holding the seat. */
  #reconnect(): void {
    const now = Date.now();
    this.#lostAt ??= now;
    if (now - this.#lostAt >= RECONNECT_WINDOW_MS) {
      // Still watching: coming back to the page or the network rejoins by itself (#644).
      this.status.value = { kind: "closed", code: 1006, reason: "reconnect_timeout" };
      return;
    }
    if (this.status.value.kind !== "reconnecting") {
      this.status.value = { kind: "reconnecting", since: this.#lostAt };
      this.#watch();
    }
    this.#retry(BACKOFF_MS[Math.min(this.#attempt, BACKOFF_MS.length - 1)] ?? 1000);
    this.#attempt++;
  }

  #retry(waitMs = 0): void {
    clearTimeout(this.#retryTimer);
    this.#retryTimer = setTimeout(() => {
      this.#ws = null;
      void this.open();
    }, waitMs);
  }

  /** Coming back to the page or the network: check the socket, or try again at once. */
  #watch(): void {
    if (this.#watching) return;
    this.#watching = true;
    document.addEventListener("visibilitychange", this.#wake);
    window.addEventListener("online", this.#wake);
  }

  #unwatch(): void {
    this.#watching = false;
    document.removeEventListener("visibilitychange", this.#wake);
    window.removeEventListener("online", this.#wake);
  }

  #startChecking(): void {
    this.#stopChecking();
    this.#liveness = new LivenessCheck(Date.now());
    this.#watchdog = setInterval(() => {
      const what = this.#liveness?.check(Date.now());
      if (what === "ping") this.#ping();
      else if (what === "dead") this.#giveUp();
    }, CHECK_EVERY_MS);
  }

  #stopChecking(): void {
    clearInterval(this.#watchdog);
    this.#liveness = null;
  }

  /** The room answers without waking up (its auto-response). */
  #ping(): void {
    if (this.#ws?.readyState === WebSocket.OPEN) this.#ws.send(PING);
  }

  /** The socket went silent without closing: leave it, and ask for the seat back on a new one. */
  #giveUp(): void {
    const ws = this.#ws;
    this.#stopChecking();
    this.#ws = null; // its close, whenever that comes, isn't ours to handle any more
    ws?.close(1000, "no answer");
    this.#reconnect();
  }
}

/** What the api's refusals of a room token mean to people (P2-API-03, P2-SAFE-01). */
const REFUSALS: Record<string, string | undefined> = {
  nickname_required: "Choose a name first.",
  room_closed: "This room has closed.",
  invite_invalid: "This invite link no longer works. Ask for a new one.",
  // A room's address without its invite (the host's address bar drops it once they're in).
  not_member: "To join this room you need its invite link. Ask someone in the room to send it.",
  // Someone in the room blocked them, or they blocked someone in it: not said who.
  blocked: "You can't join this room.",
  // Suspended or banned by Obean (#384).
  on_hold: ON_HOLD,
  rate_limited: "Too many tries at once. Wait a minute, then reload the page.",
};

/**
 * Whether the room can be joined again after this end (#644): the connection was lost (the
 * reconnect window ran out, or the network), or the api couldn't be reached. Not when the room is
 * full or closed, the host removed them, another tab took over, or Obean needs a reload.
 */
export function canRejoin(status: ConnectionStatus): boolean {
  if (status.kind !== "closed") return false;
  if (status.code === 0) return status.reason === "network";
  return status.reason === "reconnect_timeout" || closeAction(status.code) === "reconnect";
}

/** What to tell people when a room connection ends, by close code (or api error). */
export function closedMessage(status: Extract<ConnectionStatus, { kind: "closed" }>): string {
  switch (status.code) {
    case CloseCode.roomFull:
      return "This room is full.";
    case CloseCode.roomClosed:
      return "This room has closed.";
    case CloseCode.replaced:
      return "You opened this room in another tab.";
    case CloseCode.reloadRequired:
      return "Obean was updated. Reload the page to keep playing.";
    case CloseCode.kicked:
      return "The host removed you from this room.";
    case 0:
      return (
        REFUSALS[status.reason] ?? "Couldn't join the room. Check your connection and try again."
      );
    default:
      return "The connection to the room was lost.";
  }
}
